import { Task } from "@freeconvert/freeconvert-node/dist/types";
import { notNullish } from "client/hooks/utils/notNullish";
import { get } from "client/services/Backend/get";
import { formatBytes } from "client/utils/formatBytes";
import { getFCUrl } from "client/utils/getFCUrl";
import { getFileExtFromTask } from "client/utils/getFileExtFromTask";
import { getFileNameFromTask } from "client/utils/getFileNameFromTask";
import { getRandomId } from "client/utils/getRandomId";
import { getSocket } from "client/utils/getSocket";
import { translateFunc } from "client/utils/translateFunc";
import { useToast } from "components/common/Toast/useToast";
import { addImage } from "store/features/compress/actions";
import { CompressImage } from "store/features/compress/types";
import { INITIAL_COMPRESS_QUALITY } from "store/features/compress/utils";
import { store, useAppDispatch } from "store/store";
import { ImageExtension } from "store/types/imageExtension";
import { useCompress } from "./useCompress";

export const useUrlTaskIds = () => {
    const dispatch = useAppDispatch();
    const { compressImage } = useCompress();
    const { toast, removeToast } = useToast();

    const toastText = translateFunc("loading-your-images");

    const handleUrlTaskIds = async (urlTaskIds: string[]) => {
        const toastId = toast({
            closeable: true,
            position: "top-right",
            status: "success",
            text: toastText,
        });
        const socket = await getSocket();
        if (!socket.connected) {
            await new Promise((resolve) => {
                socket.on("connect", () => {
                    resolve(null);
                });
            });
        }

        (await getTasks(urlTaskIds))
            .filter(notNullish)
            .map(createNewImage)
            .filter(notNullish)
            .forEach(async (image) => {
                if (!image.importTask) return;
                compressImage(
                    image,
                    image.importTask,
                    {
                        type: "quality",
                        maxFileSize: { value: 0 },
                        quality: { value: INITIAL_COMPRESS_QUALITY },
                    },
                    image.file.file.name,
                );
            });
        removeToast(toastId);
    };

    const getTasks = async (urlTaskIds: string[]) =>
        await Promise.all(
            urlTaskIds.map(async (taskId) => {
                const task = await getTaskData(taskId);
                if (!task || task.status !== "completed" || !task.result) return;
                return task;
            }),
        );

    const createNewImage = (task: Task): CompressImage | undefined => {
        const result = task.result;
        if (!result) return;
        const size = formatBytes(result.size || 0);
        const compressionSettings = store.getState().compress.compressionSettings;
        const image: CompressImage = {
            title: getFileNameFromTask(task),
            createdAt: new Date(task.createdAt),
            file: {
                file: new File([], getFileNameFromTask(task)),
                src: result.url?.toString() || "",
                size,
            },
            format: getFileExtFromTask(task) as ImageExtension,
            hasExpired: false,
            id: getRandomId(),
            importTask: task,
            original: {
                width: 0,
                height: 0,
                size,
            },
            preview: {
                width: 0,
                height: 0,
                size,
            },
            uploadType: "url",
            settings: { url: result.url },
            src: getFCUrl(result.url?.toString() || ""),
            quality: INITIAL_COMPRESS_QUALITY,
            sliderValue: INITIAL_COMPRESS_QUALITY,
            state: "imported",
            compressionSettings,
        };
        dispatch(addImage(image));
        return image;
    };

    const getTaskData = async (taskId: string): Promise<Task | null> => {
        try {
            const { error, result } = await get<Task>(`${process.env.NEXT_PUBLIC_BE_URL}/tasks/${taskId}`);
            if (error) return null;
            return result;
        } catch (e) {
            return null;
        }
    };

    return {
        handleUrlTaskIds,
    };
};
